import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Space, Table, Tag, Typography } from 'antd';
import PageTitle from '../../components/PageTitle';
import {
  huifuToolsQueryBalance,
  huifuToolsQueryUser,
  huifuToolsQueryWithdrawRecords,
  huifuToolsSetManualWithdraw,
} from '../../api';

const { Text } = Typography;
const { TextArea } = Input;

const formatJson = (value) => {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

const defaultBeginDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return formatYmd(date);
};

const defaultEndDate = () => formatYmd(new Date());

const STATUS_MAP = {
  S: { text: '成功', color: 'success' },
  F: { text: '失败', color: 'error' },
  I: { text: '处理中', color: 'processing' },
  P: { text: '处理中', color: 'warning' },
};

const formatDisplayDate = (ymd) => {
  const s = String(ymd || '').trim();
  if (s.length !== 8) return s || '-';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
};

const toMoney = (value) => {
  const n = Number.parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) => `¥${toMoney(value).toFixed(2)}`;

const renderStatus = (code) => {
  const key = String(code || '').trim().toUpperCase();
  const item = STATUS_MAP[key];
  if (!item) return key || '-';
  return <Tag color={item.color}>{item.text}</Tag>;
};

const normalizeAutoRecord = (item, index) => ({
  key: `auto-${item.trans_id || index}`,
  withdrawType: '自动',
  date: item.trans_date || '',
  dateSort: item.trans_date || '',
  amount: toMoney(item.trans_amt),
  fee: toMoney(item.fee_amt),
  status: item.trans_stat || '',
  cardNo: item.card_no || '',
  cardName: item.card_name || '',
  transactionId: item.trans_id || '',
});

const normalizeManualRecord = (item, index) => ({
  key: `manual-${item.org_hf_seq_id || index}`,
  withdrawType: '手动',
  date: item.org_req_date || '',
  dateSort: item.org_req_date || '',
  amount: toMoney(item.cash_amt),
  fee: toMoney(item.fee_amt),
  status: item.trans_status || '',
  cardNo: item.card_no || '',
  cardName: item.card_name || '',
  transactionId: item.org_hf_seq_id || '',
});

const buildWithdrawTableView = (res) => {
  const payload = res?.data;
  if (!payload) return null;

  const rows = [];
  const autoRecords = Array.isArray(payload.auto?.records) ? payload.auto.records : [];
  const manualRecords = Array.isArray(payload.manual?.records) ? payload.manual.records : [];

  autoRecords.forEach((item, index) => rows.push(normalizeAutoRecord(item, index)));
  manualRecords.forEach((item, index) => rows.push(normalizeManualRecord(item, index)));

  rows.sort((a, b) => String(b.dateSort).localeCompare(String(a.dateSort)));

  const summary = payload.summary || {};
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);
  const totalFee = rows.reduce((sum, row) => sum + row.fee, 0);

  return {
    meta: {
      huifuId: payload.huifu_id || '',
      beginDate: payload.begin_date || '',
      endDate: payload.end_date || '',
      queryType: payload.query_type || '',
    },
    summary: {
      totalCount: rows.length,
      autoCount: summary.auto_count ?? autoRecords.length,
      manualCount: summary.manual_count ?? manualRecords.length,
      autoTotalAmt: summary.auto_total_amt ?? autoRecords.reduce((s, i) => s + toMoney(i.trans_amt), 0),
      manualTotalAmt: summary.manual_total_amt ?? manualRecords.reduce((s, i) => s + toMoney(i.cash_amt), 0),
      totalAmount,
      totalFee,
    },
    rows,
    errors: [payload.auto_error, payload.manual_error].filter(Boolean),
  };
};

const buildBalanceTableView = (res) => {
  const payload = res?.data;
  if (!payload) return null;

  const accounts = Array.isArray(payload.accounts) ? payload.accounts : [];
  const summary = payload.summary || {};

  const rows = accounts.map((item, index) => ({
    key: `acct-${item.acct_id || item.acct_type || index}`,
    acctType: item.acct_type || '',
    acctId: item.acct_id || '',
    acctName: item.acct_name || '',
    avlBal: toMoney(item.avl_bal),
    balAmt: toMoney(item.bal_amt ?? item.bal),
    frzBal: toMoney(item.frz_bal),
    raw: item,
  }));

  return {
    meta: {
      huifuId: payload.huifu_id || '',
      respCode: summary.resp_code || payload.response?.resp_code || '',
      respDesc: summary.resp_desc || payload.response?.resp_desc || '',
    },
    summary: {
      accountCount: summary.account_count ?? rows.length,
      totalAvlBal: summary.total_avl_bal ?? rows.reduce((s, r) => s + r.avlBal, 0),
      totalBal: summary.total_bal ?? rows.reduce((s, r) => s + r.balAmt, 0),
      totalFrzBal: summary.total_frz_bal ?? rows.reduce((s, r) => s + r.frzBal, 0),
    },
    rows,
    error: payload.call_error || '',
  };
};

const withdrawColumns = [
  {
    title: '类型',
    dataIndex: 'withdrawType',
    width: 72,
    render: (text) => (
      <Tag color={text === '自动' ? 'blue' : 'purple'}>{text}</Tag>
    ),
  },
  {
    title: '日期',
    dataIndex: 'date',
    width: 110,
    render: (text) => formatDisplayDate(text),
  },
  {
    title: '提现金额',
    dataIndex: 'amount',
    width: 110,
    align: 'right',
    render: (value) => <Text strong>{formatMoney(value)}</Text>,
  },
  {
    title: '手续费',
    dataIndex: 'fee',
    width: 100,
    align: 'right',
    render: (value) => formatMoney(value),
  },
  {
    title: '状态',
    dataIndex: 'status',
    width: 90,
    render: (value) => renderStatus(value),
  },
  {
    title: '银行卡',
    dataIndex: 'cardNo',
    width: 170,
    ellipsis: true,
  },
  {
    title: '户名',
    dataIndex: 'cardName',
    width: 100,
    ellipsis: true,
  },
  {
    title: '流水号',
    dataIndex: 'transactionId',
    ellipsis: true,
  },
];

const balanceColumns = [
  {
    title: '账户类型',
    dataIndex: 'acctType',
    width: 100,
  },
  {
    title: '账户ID',
    dataIndex: 'acctId',
    width: 180,
    ellipsis: true,
  },
  {
    title: '账户名称',
    dataIndex: 'acctName',
    width: 140,
    ellipsis: true,
  },
  {
    title: '可用余额',
    dataIndex: 'avlBal',
    width: 120,
    align: 'right',
    render: (value) => <Text strong type="success">{formatMoney(value)}</Text>,
  },
  {
    title: '账户余额',
    dataIndex: 'balAmt',
    width: 120,
    align: 'right',
    render: (value) => formatMoney(value),
  },
  {
    title: '冻结余额',
    dataIndex: 'frzBal',
    width: 120,
    align: 'right',
    render: (value) => formatMoney(value),
  },
];

const HuifuApiTools = () => {
  const [huifuId, setHuifuId] = useState('');
  const [beginDate, setBeginDate] = useState(defaultBeginDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState('');
  const [withdrawView, setWithdrawView] = useState(null);
  const [balanceView, setBalanceView] = useState(null);

  const dateHint = useMemo(
    () => `${beginDate || '默认近30天'} ~ ${endDate || '今天'}，格式 yyyyMMdd`,
    [beginDate, endDate],
  );

  const clearOutput = () => {
    setOutput('');
    setWithdrawView(null);
    setBalanceView(null);
  };

  const run = async (action) => {
    const id = String(huifuId || '').trim();
    if (!id) {
      setOutput('请先输入 huifu_id');
      setWithdrawView(null);
      setBalanceView(null);
      return;
    }
    setLoading(true);
    setOutput('请求中...');
    setWithdrawView(null);
    setBalanceView(null);
    try {
      let res;
      if (action === 'query') {
        res = await huifuToolsQueryUser(id);
      } else if (action === 'balance') {
        res = await huifuToolsQueryBalance(id);
      } else if (action === 'manual') {
        res = await huifuToolsSetManualWithdraw(id);
      } else {
        res = await huifuToolsQueryWithdrawRecords({
          huifu_id: id,
          begin_date: String(beginDate || '').trim(),
          end_date: String(endDate || '').trim(),
          query_type: action === 'withdraw_auto' ? 'auto' : action === 'withdraw_manual' ? 'manual' : 'all',
        });
      }
      setOutput(formatJson(res));
      if (action === 'balance') {
        setBalanceView(buildBalanceTableView(res));
      } else if (action === 'withdraw_all' || action === 'withdraw_auto' || action === 'withdraw_manual') {
        setWithdrawView(buildWithdrawTableView(res));
      }
    } catch (err) {
      const body = err.response?.data;
      setOutput(formatJson(body || { error: err.message || '请求失败' }));
      setWithdrawView(null);
      setBalanceView(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="汇付接口工具" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="调试说明"
        description={
          <div>
            <div>输入汇付 huifu_id 后调用接口，下方展示表格汇总与原始 JSON。</div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                「查询」= 汇付 user/basicdata/query + 本地 fangdong_ext / withdraw_mode / 银行卡；
                「查余额」= 汇付 acctpayment/balance/query；
                「查提现记录」= 汇付结算查询（自动）+ 出款批量查询（手动，后端按 3 天分段）；
                「改为仅手动提现」= 汇付 user/busi/modify（settle_status=0）+ 本地 withdraw_mode 写 2。
              </Text>
            </div>
          </div>
        }
      />

      <Card title="参数" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>huifu_id</Text>
            </div>
            <Input
              placeholder="请输入 18 位汇付用户编号"
              value={huifuId}
              onChange={(e) => setHuifuId(e.target.value)}
              maxLength={32}
              allowClear
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>提现查询日期范围</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {dateHint}
              </Text>
            </div>
            <Space wrap>
              <Input
                placeholder="开始日期 yyyyMMdd"
                value={beginDate}
                onChange={(e) => setBeginDate(e.target.value.replace(/\D/g, '').slice(0, 8))}
                style={{ width: 180 }}
                allowClear
              />
              <Text type="secondary">至</Text>
              <Input
                placeholder="结束日期 yyyyMMdd"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value.replace(/\D/g, '').slice(0, 8))}
                style={{ width: 180 }}
                allowClear
              />
              <Button
                disabled={loading}
                onClick={() => {
                  setBeginDate(defaultBeginDate());
                  setEndDate(defaultEndDate());
                }}
              >
                重置为近30天
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Button type="primary" loading={loading} onClick={() => run('query')}>
              查询当前汇付配置
            </Button>
            <Button loading={loading} onClick={() => run('balance')}>
              查账户余额
            </Button>
            <Button loading={loading} onClick={() => run('withdraw_all')}>
              查全部提现记录
            </Button>
            <Button loading={loading} onClick={() => run('withdraw_auto')}>
              查自动提现（结算）
            </Button>
            <Button loading={loading} onClick={() => run('withdraw_manual')}>
              查手动提现（出款）
            </Button>
            <Button loading={loading} danger onClick={() => run('manual')}>
              改为仅手动提现
            </Button>
            <Button disabled={loading} onClick={clearOutput}>
              清空输出
            </Button>
          </Space>
        </Space>
      </Card>

      {balanceView && (
        <Card
          title="账户余额（表格）"
          style={{ marginBottom: 16 }}
          extra={(
            <Space size="large" wrap>
              <Text type="secondary">{balanceView.meta.huifuId}</Text>
              <Text>
                可用余额合计 <Text strong type="success" style={{ fontSize: 18 }}>{formatMoney(balanceView.summary.totalAvlBal)}</Text>
              </Text>
              <Text type="secondary">
                账户 {balanceView.summary.accountCount} 个
              </Text>
              {balanceView.meta.respCode && (
                <Text type="secondary">
                  {balanceView.meta.respCode}
                  {balanceView.meta.respDesc ? ` / ${balanceView.meta.respDesc}` : ''}
                </Text>
              )}
            </Space>
          )}
        >
          {balanceView.error && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="查询失败"
              description={balanceView.error}
            />
          )}
          <Table
            rowKey="key"
            columns={balanceColumns}
            dataSource={balanceView.rows}
            size="small"
            pagination={false}
            scroll={{ x: 860 }}
            locale={{ emptyText: '未查询到账户余额信息' }}
            summary={() => (
              balanceView.rows.length > 0 ? (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>合计</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong type="success">{formatMoney(balanceView.summary.totalAvlBal)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {formatMoney(balanceView.summary.totalBal)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {formatMoney(balanceView.summary.totalFrzBal)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            )}
          />
        </Card>
      )}

      {withdrawView && (
        <Card
          title="提现记录（表格）"
          style={{ marginBottom: 16 }}
          extra={(
            <Space size="large" wrap>
              <Text type="secondary">
                {withdrawView.meta.huifuId}
                {' · '}
                {formatDisplayDate(withdrawView.meta.beginDate)}
                {' ~ '}
                {formatDisplayDate(withdrawView.meta.endDate)}
              </Text>
              <Text>
                共 <Text strong>{withdrawView.summary.totalCount}</Text> 笔
              </Text>
              <Text>
                合计 <Text strong type="danger">{formatMoney(withdrawView.summary.totalAmount)}</Text>
              </Text>
              <Text type="secondary">
                自动 {withdrawView.summary.autoCount} 笔 / 手动 {withdrawView.summary.manualCount} 笔
              </Text>
            </Space>
          )}
        >
          {withdrawView.errors.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="部分查询有错误"
              description={withdrawView.errors.join('；')}
            />
          )}
          <Table
            rowKey="key"
            columns={withdrawColumns}
            dataSource={withdrawView.rows}
            size="small"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            scroll={{ x: 980 }}
            locale={{ emptyText: '该日期范围内无提现记录' }}
            summary={() => (
              withdrawView.rows.length > 0 ? (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>合计</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong type="danger">{formatMoney(withdrawView.summary.totalAmount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {formatMoney(withdrawView.summary.totalFee)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={4} />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            )}
          />
        </Card>
      )}

      <Card title="接口返回（原始 JSON）">
        <TextArea
          value={output}
          readOnly
          placeholder="点击上方按钮后，完整返回会显示在这里"
          autoSize={{ minRows: 16, maxRows: 32 }}
          style={{
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: 12,
            background: '#fafafa',
          }}
        />
      </Card>
    </div>
  );
};

export default HuifuApiTools;
